"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeActivityListRows = computeActivityListRows;
const evaluateActivityPlayable_1 = require("./evaluateActivityPlayable");
const openCharacterRuntime_1 = require("../utils/openCharacterRuntime");
function pickNavigationSceneId(def, isDone, resumeScenes, validSceneIds) {
    const base = def.startSceneId;
    if (isDone || !resumeScenes || resumeScenes.size === 0) {
        return base;
    }
    const r = resumeScenes.get(def.id);
    if (r && validSceneIds.has(r)) {
        return r;
    }
    return base;
}
function buildValidSceneIdSet(adventure) {
    return new Set((adventure.scenes ?? []).map(s => s.id).filter(Boolean));
}
function matchesEmbedFilter(a, embed) {
    if (embed.types && embed.types.length > 0 && !embed.types.includes(a.typeId)) {
        return false;
    }
    if (embed.parentActivityId !== undefined) {
        if (a.parentActivityId !== embed.parentActivityId) {
            return false;
        }
    }
    else {
        if (a.parentActivityId) {
            return false;
        }
    }
    return true;
}
function isEligible(a, characterName) {
    return (0, openCharacterRuntime_1.characterMatchesEligibleList)(a.eligibleCharacterNames, characterName);
}
/** Display name for a prerequisite activity id, or the raw id if not found. */
function requirementActivityLabel(all, reqId) {
    const def = all.find((x) => x.id === reqId);
    const n = def?.name?.trim();
    return n || reqId;
}
function hasRenderableActivityDescription(desc) {
    if (desc === undefined || desc === null) {
        return false;
    }
    if (typeof desc === 'string') {
        return desc.trim().length > 0;
    }
    return Array.isArray(desc.elements) && desc.elements.length > 0;
}
/**
 * Build ordered activity list rows for an `<Activity-List>` embed, including disabled rows
 * with per-requirement met/unmet detail.
 *
 * When {@link resumeScenes} is provided, a non-completed activity uses the mapped scene id
 * for {@link ActivityListRow.startSceneId} (the navigation target from the office list) if
 * it exists in the adventure; otherwise the activity's configured start scene is used.
 */
function computeActivityListRows(adventure, characterName, state, embed, evaluateRequiresScript, resumeScenes) {
    const all = adventure.activities || [];
    const { inProgress, completed } = state;
    const validSceneIds = buildValidSceneIdSet(adventure);
    const candidates = all.filter(a => matchesEmbedFilter(a, embed) && isEligible(a, characterName));
    const sortKey = embed.sort === 'name' ? 'name' : 'list_order';
    const groupCompleted = embed.groupCompleted !== false;
    const sorted = [...candidates].sort((a, b) => {
        if (groupCompleted) {
            const aDone = completed.has(a.id) ? 1 : 0;
            const bDone = completed.has(b.id) ? 1 : 0;
            if (aDone !== bDone) {
                return aDone - bDone;
            }
        }
        if (sortKey === 'name') {
            return a.name.localeCompare(b.name);
        }
        if (a.listOrder !== b.listOrder) {
            return a.listOrder - b.listOrder;
        }
        return a.id.localeCompare(b.id);
    });
    const rows = [];
    for (const a of sorted) {
        const requirements = [];
        const isDone = completed.has(a.id);
        const relevantRequiresCompleted = (0, evaluateActivityPlayable_1.filterRequiresCompletedForCharacter)(a.requiresCompleted, all, characterName);
        for (const reqId of relevantRequiresCompleted) {
            const met = completed.has(reqId);
            requirements.push({
                key: `completed:${reqId}`,
                met,
                label: `Complete: ${requirementActivityLabel(all, reqId)}`
            });
        }
        const relevantRequiresActive = (0, evaluateActivityPlayable_1.filterRequiresActiveForCharacter)(a.requiresActive, all, characterName);
        for (const reqId of relevantRequiresActive) {
            const met = inProgress.has(reqId);
            requirements.push({
                key: `active:${reqId}`,
                met,
                label: `Active: ${requirementActivityLabel(all, reqId)}`
            });
        }
        let scriptMet = true;
        if (a.requiresScript && a.requiresScript.trim()) {
            scriptMet = evaluateRequiresScript ? evaluateRequiresScript(a.requiresScript) : true;
            requirements.push({
                key: 'script',
                met: scriptMet,
                label: 'Requirements script'
            });
        }
        if (isDone) {
            requirements.push({
                key: 'done',
                met: true,
                label: 'Completed'
            });
        }
        const prereqsOk = relevantRequiresCompleted.every((reqId) => completed.has(reqId)) &&
            relevantRequiresActive.every((reqId) => inProgress.has(reqId)) &&
            scriptMet;
        const enabled = !isDone && prereqsOk;
        const isInProgress = inProgress.has(a.id) && !isDone;
        if (embed.hideLocked && !isDone && !prereqsOk) {
            continue;
        }
        if (isDone && a.hideWhenCompleted) {
            continue;
        }
        rows.push({
            activityId: a.id,
            name: a.name,
            typeId: a.typeId,
            startSceneId: pickNavigationSceneId(a, isDone, resumeScenes, validSceneIds),
            enabled,
            isInProgress,
            requirements,
            ...(hasRenderableActivityDescription(a.description) && !isDone ? { description: a.description } : {})
        });
    }
    return rows;
}
//# sourceMappingURL=computeActivityListRows.js.map